import { useState } from 'react'
import { useAdminAuth } from '../../shared/auth/admin-auth-context'
import { Kpi } from '../../shared/ui/charts/kpi'
import { formatNumber } from '../../shared/ui/charts/scale'
import { BalancesTab } from './balances-tab'
import { ChecksTab } from './checks-tab'
import { OverviewTab } from './overview-tab'
import { PaymentsTab } from './payments-tab'
import { PromoTab } from './promo-tab'
import { ReceiptsTab } from './receipts-tab'
import { PERIODS, useAuditRuns, useLunarOverview, useLunarPayments, useReceipts } from './queries'
import type { PeriodDays } from './queries'
import styles from './lunars.module.css'

type Tab = 'overview' | 'payments' | 'receipts' | 'balances' | 'promo' | 'checks'
const TABS: ReadonlyArray<{ id: Tab; label: string }> = [
  { id: 'overview', label: 'Обзор' },
  { id: 'payments', label: 'Платежи' },
  { id: 'receipts', label: 'Чеки' },
  { id: 'balances', label: 'Балансы' },
  { id: 'promo', label: 'Промо' },
  { id: 'checks', label: 'Проверки' },
]

export function LunarsPage() {
  const { user, status } = useAdminAuth()
  const isAdmin = status === 'authenticated' && user?.role === 'admin'
  const [tab, setTab] = useState<Tab>('overview')
  const [period, setPeriod] = useState<PeriodDays>(30)
  const overview = useLunarOverview(period, isAdmin)
  const payments = useLunarPayments(period, isAdmin && tab === 'payments')
  const runs = useAuditRuns(isAdmin)
  const receipts = useReceipts('todo', 0, isAdmin)

  if (!isAdmin) return <p className="error-box">Раздел доступен только администратору.</p>

  const open = runs.data?.open_findings ?? 0
  const todoReceipts = receipts.data?.todo_count ?? 0
  const overdueReceipts = receipts.data?.overdue_count ?? 0
  const data = overview.data
  const withPeriod = tab === 'overview' || tab === 'payments'

  return (
    <div>
      <header className={styles.head}>
        <div>
          <h1>Лунары</h1>
          <p className="muted">Эмиссия, платежи, чеки, балансы, промо тарологов и сверка происхождения. Время московское.</p>
        </div>
        {withPeriod && (
          <div className={styles.periods} role="radiogroup" aria-label="Период">
            {PERIODS.map((p) => (
              <button
                key={p}
                role="radio"
                aria-checked={period === p}
                className={period === p ? styles.periodActive : styles.period}
                onClick={() => setPeriod(p)}
              >
                {p} дней
              </button>
            ))}
          </div>
        )}
      </header>

      {open > 0 && tab !== 'checks' && (
        <div className={styles.banner}>
          <span>Открытых находок сверки: {open}</span>
          <button className="secondary" onClick={() => setTab('checks')}>
            Открыть проверки
          </button>
        </div>
      )}

      {overdueReceipts > 0 && tab !== 'receipts' && (
        <div className={styles.banner}>
          <span>Оплат без чека дольше суток: {overdueReceipts}</span>
          <button className="secondary" onClick={() => setTab('receipts')}>
            Открыть чеки
          </button>
        </div>
      )}

      {data && (
        <div className={styles.kpis}>
          <Kpi label="В обращении" value={formatNumber(data.circulation_total)} hint={`куплено ${formatNumber(data.circulation_paid)}`} />
          <Kpi label="Куплено за период" value={`+${formatNumber(data.bought)}`} />
          <Kpi label="Бесплатно за период" value={`+${formatNumber(data.earned_free)}`} />
          <Kpi label="Потрачено" value={`−${formatNumber(data.spent)}`} />
          <Kpi label="Обязательство" value={`≈ ${formatNumber(data.paid_liability_rub)} ₽`} hint="по купленным лунарам" />
        </div>
      )}

      <nav className={styles.tabs} aria-label="Разделы">
        {TABS.map((t) => (
          <button key={t.id} className={tab === t.id ? styles.tabActive : styles.tab} onClick={() => setTab(t.id)}>
            {t.label}
            {t.id === 'checks' && open > 0 && <span className="badge danger">{open}</span>}
            {t.id === 'receipts' && todoReceipts > 0 && <span className={overdueReceipts > 0 ? 'badge danger' : 'badge'}>{todoReceipts}</span>}
          </button>
        ))}
      </nav>

      {overview.isError && <p className="error-box">Не удалось загрузить данные.</p>}
      {overview.isPending && <div className="spinner" aria-label="Загрузка" />}
      {tab === 'overview' && data && <OverviewTab data={data} />}
      {tab === 'payments' && payments.data && <PaymentsTab data={payments.data} />}
      {tab === 'payments' && payments.isPending && <div className="spinner" aria-label="Загрузка" />}
      {tab === 'receipts' && <ReceiptsTab />}
      {tab === 'balances' && <BalancesTab />}
      {tab === 'promo' && <PromoTab />}
      {tab === 'checks' && <ChecksTab />}
    </div>
  )
}
